import React from 'react';
import ReactDOM from 'react-dom';
import { useTutorial } from '../context/TutorialContext';
import { useTutorialPosition } from '../hooks/useTutorialPosition';
import './TutorialOverlay.css';

const PADDING = 8; // spotlight padding around target element
const SPOTLIGHT_RADIUS = 12; // rounded corners on the cutout

const TutorialOverlay = () => {
  const { isActive, getCurrentStep, isStepVisibleOnRoute } = useTutorial();
  const step = getCurrentStep();
  const position = useTutorialPosition(step?.targetSelector, step);

  if (!isActive || !isStepVisibleOnRoute() || !position) return null;

  const { targetRect } = position;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Base spotlight rect with padding
  let x = Math.max(0, targetRect.left - PADDING);
  let y = Math.max(0, targetRect.top - PADDING);
  let w = targetRect.width + PADDING * 2;
  let h = targetRect.height + PADDING * 2;

  // Step-specific adjustments so the lit-up area aligns with the UI
  if (step?.id === 'copy-button') {
    x += 26 - w / 4; // Shift right then a bit back left (~1/3 of spotlight width)
  } else if (step?.id === 'discover-tab') {
    const cutLeft = w * 0.2;
    x += cutLeft;
    w -= cutLeft;
  }

  const r = Math.min(SPOTLIGHT_RADIUS, w / 2, h / 2);

  // SVG path: full-screen rect with a rounded-rectangle hole cut out (clockwise inner path)
  const hole = r <= 0
    ? `M${x},${y} H${x + w} V${y + h} H${x} Z`
    : `M${x + r},${y} L${x + w - r},${y} A${r},${r} 0 0 1 ${x + w},${y + r} L${x + w},${y + h - r} A${r},${r} 0 0 1 ${x + w - r},${y + h} L${x + r},${y + h} A${r},${r} 0 0 1 ${x},${y + h - r} L${x},${y + r} A${r},${r} 0 0 1 ${x + r},${y} Z`;
  const clipPath = `M0,0 H${vw} V${vh} H0 Z ${hole}`;

  // On step one (responsibility card), block clicks on the lit-up card so user must use the OK button.
  // Blocker is a sibling in the portal so it can use pointer-events: auto (overlay has pointer-events: none).
  const blockHoleClicks = step?.id === 'responsibility-card';

  return ReactDOM.createPortal(
    <>
      <div className="tutorial-overlay" aria-hidden="true">
        <svg
          className="tutorial-overlay-svg"
          width={vw}
          height={vh}
          viewBox={`0 0 ${vw} ${vh}`}
        >
          <path
            d={clipPath}
            fillRule="evenodd"
            className="tutorial-overlay-path"
          />
        </svg>
      </div>
      {blockHoleClicks && (
        <div
          className="tutorial-overlay-hole-blocker"
          style={{
            left: x,
            top: y,
            width: w,
            height: h,
            borderRadius: r,
          }}
          aria-hidden="true"
        />
      )}
    </>,
    document.body
  );
};

export default TutorialOverlay;
